import { Box, Divider, Stack, Text } from '@mantine/core';

import { AbstractCard } from '@/module/Author/Abstract/components/Card/AbstractCard';
import { ArticleImpactFactor } from '@/module/Author/Article/components/ArticleImpactFactor';
import { ArticleInfluence } from '@/module/Author/Article/components/ArticleInfluence';
import { ArticleCard } from '@/module/Author/Article/components/Card/ArticleCard';
import { AwardCard } from '@/module/Author/Award/components/Card/AwardCard';
import { BookCard } from '@/module/Author/Book/components/Card/BookCard';
import { ChapterCard } from '@/module/Author/Chapter/components/Card/ChapterCard';
import { CitationCard } from '@/module/Author/Citation/components/Card/CitationCard';
import { CommitteeCard } from '@/module/Author/Committee/components/Card/CommitteeCard';
import { CourseCard } from '@/module/Author/Course/components/Card/CourseCard';
import { PatentCard } from '@/module/Author/Patent/components/Card/PatentCard';
import { ProceedingCard } from '@/module/Author/Proceeding/components/Card/ProceedingCard';
import { ProjectCard } from '@/module/Author/Project/components/Card/ProjectCard';
import { TranslationCard } from '@/module/Author/Translation/components/Card/TranslationCard';

import { assertUnreachable } from '@feature/utils';

import {
  IndicatorItem,
  isMinStdIndicatorAbstractItem,
  isMinStdIndicatorArticleItem,
  isMinStdIndicatorAwardItem,
  isMinStdIndicatorBookItem,
  isMinStdIndicatorBooleanItem,
  isMinStdIndicatorChapterItem,
  isMinStdIndicatorCitationItem,
  isMinStdIndicatorCommitteeItem,
  isMinStdIndicatorCourseItem,
  isMinStdIndicatorFreeItem,
  isMinStdIndicatorIntervalItem,
  isMinStdIndicatorNumberItem,
  isMinStdIndicatorPatentItem,
  isMinStdIndicatorProceedingItem,
  isMinStdIndicatorProjectItem,
  isMinStdIndicatorTranslationItem,
} from './../../model/MinStdTypes';

interface MinStdItemCardHeaderProps {
  item: IndicatorItem;
  legend: string | null;
}

export function MinStdItemCardHeader({ item, legend }: MinStdItemCardHeaderProps) {
  if (isMinStdIndicatorFreeItem(item)) {
    return <Text>{item.activityDescription}</Text>;
  }

  if (isMinStdIndicatorBooleanItem(item)) {
    return <Text fw="500">DA</Text>;
  }

  if (isMinStdIndicatorNumberItem(item)) {
    return (
      <Text>
        {legend}: {item.number}
      </Text>
    );
  }

  if (isMinStdIndicatorIntervalItem(item)) {
    const interval = item.interval;
    return (
      <Stack gap="sm">
        <Text>{interval.activity}</Text>
        <Text c="dimmed">
          {interval.startYear} - {interval.endYear ?? 'an curent'}
        </Text>
      </Stack>
    );
  }

  if (isMinStdIndicatorCourseItem(item)) {
    return (
      <CourseCard
        course={item.course}
        onClick={() => window.open(`/author/course/${item.course.id}`)}
      />
    );
  }

  if (isMinStdIndicatorAbstractItem(item)) {
    return (
      <AbstractCard
        abstract={item.abstractItem}
        onClick={() => window.open(`/author/abstract/${item.abstractItem.id}`)}
      />
    );
  }

  if (isMinStdIndicatorArticleItem(item)) {
    return (
      <Box flex={1}>
        <ArticleCard
          article={item.article}
          onClick={() => window.open(`/author/article/${item.article.id}`)}
        />
        <Box p="10px">
          <ArticleImpactFactor
            hideTemporary
            articleId={item.article.id}
          />
          <Divider my="10px" />
          <ArticleInfluence
            hideTemporary
            articleId={item.article.id}
          />
        </Box>
      </Box>
    );
  }

  if (isMinStdIndicatorBookItem(item)) {
    return (
      <BookCard
        book={item.book}
        onClick={() => window.open(`/author/book/${item.book.id}`)}
      />
    );
  }

  if (isMinStdIndicatorTranslationItem(item)) {
    return (
      <TranslationCard
        translation={item.translation}
        onClick={() => window.open(`/author/translation/${item.translation.id}`)}
      />
    );
  }

  if (isMinStdIndicatorChapterItem(item)) {
    return (
      <ChapterCard
        chapter={item.chapter}
        onClick={() => window.open(`/author/chapter/${item.chapter.id}`)}
      />
    );
  }

  if (isMinStdIndicatorProceedingItem(item)) {
    return (
      <ProceedingCard
        proceeding={item.proceeding}
        onClick={() => window.open(`/author/proceeding/${item.proceeding.id}`)}
      />
    );
  }

  if (isMinStdIndicatorPatentItem(item)) {
    return (
      <PatentCard
        patent={item.patent}
        onClick={() => window.open(`/author/patent/${item.patent.id}`)}
      />
    );
  }

  if (isMinStdIndicatorProjectItem(item)) {
    return (
      <ProjectCard
        project={item.project}
        onClick={() => window.open(`/author/project/${item.project.id}`)}
      />
    );
  }

  if (isMinStdIndicatorAwardItem(item)) {
    return (
      <AwardCard
        award={item.award}
        onClick={() => window.open(`/author/award/${item.award.id}`)}
      />
    );
  }

  if (isMinStdIndicatorCommitteeItem(item)) {
    return (
      <CommitteeCard
        committee={item.committee}
        onClick={() => window.open(`/author/committee/${item.committee.id}`)}
      />
    );
  }

  if (isMinStdIndicatorCitationItem(item)) {
    return <CitationCard citation={item.citation} />;
  }

  assertUnreachable(item);
}
